import { Box, Grid, InputAdornment, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";

import { LoadingButton } from "@mui/lab";
import NumberFormats from "components/NumberFormats";
import OrderConfirmationModal from "./OrderConfirmationModal";
import PriceInput from "components/PriceInput";
import { getInvestmentPortfolioBuyAmountMax } from "store/slices/investment/investmentSlice";
import { repeat } from "utils/helpers/interval";
import useDispatch from "hooks/useDispatch";
import { useForm } from "react-hook-form";
import useNotify from "hooks/useNotify";
import { useState } from "react";
import { actionButtonBg } from "utils/helpers/actionButtonBg";

let getInvestmentPortfolioBuyAmountMaxInterval;
export default function InvestmentAmount({
  investmentDetail,
  handleClickShowMore,
}) {
  const dispatch = useDispatch();
  const notify = useNotify();
  const theme = useTheme()

  const investmentAmountRef = useRef(null)

  const [openOrderConfirmationModal, setOpenOrderConfirmationModal] =
    useState(false);
  const [investmentPortfolioBuyAmountMax, setInvestmentPortfolioBuyAmountMax] =
    useState();

  const coinName = investmentDetail?.objPortfolio?.baseCoinName;

  const methods = useForm({
    defaultValues: {
      investment: "",
    },
  });

  const { watch, setValue } = methods;

  const investment = watch("investment");

  const setErrorForMinimumAmount = useMemo(() => {
    if (
      investment &&
      +investmentPortfolioBuyAmountMax?.minAmount > +investment
    ) {
      return `حداقل مقدار سرمایه‌گذاری ${investmentPortfolioBuyAmountMax?.minAmount} ${coinName} می‌باشد.`;
    }
  }, [investmentPortfolioBuyAmountMax?.minAmount, investment, coinName]);

  const handleOpenInvestment = () => {
    const investmentIsLessThanMinimum =
      +investmentPortfolioBuyAmountMax?.minAmount > +investment;

    if (investmentIsLessThanMinimum) {
      notify(
        `حداقل مقدار سرمایه‌گذاری ${investmentPortfolioBuyAmountMax?.minAmount} ${coinName} می‌باشد.`,
        "error"
      );
    } else {
      setOpenOrderConfirmationModal(true);
    }
  };

  const handleGetInvestmentPortfolioBuyAmountMax = async () => {
    const { payload } = await dispatch(
      getInvestmentPortfolioBuyAmountMax(investmentDetail?.objPortfolio?.id)
    );

    setInvestmentPortfolioBuyAmountMax(payload?.data?.data);
  };

  useEffect(() => {
    if (investmentDetail?.objPortfolio?.validSeconds) {
      clearInterval(getInvestmentPortfolioBuyAmountMaxInterval);

      handleGetInvestmentPortfolioBuyAmountMax();

      getInvestmentPortfolioBuyAmountMaxInterval = repeat({
        action: () => handleGetInvestmentPortfolioBuyAmountMax(),
        time: investmentDetail?.objPortfolio?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getInvestmentPortfolioBuyAmountMaxInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const focusOnInput = () => {
      const investmentAmountInput = investmentAmountRef?.current?.querySelector('input')

      investmentAmountInput?.focus();
    }

    focusOnInput()
  }, [])

  return (
    <>
      <Grid item xs={12}>
        <Box>
          <Typography variant="h6" fontWeight={300} color="text.secondary">
            Investment Amount
          </Typography>

          <PriceInput
            ref={investmentAmountRef}
            value={watch("investment")}
            onChange={(e) => setValue("investment", e)}
            showPrefix={false}
            placeholder="Investment"
            max={investmentPortfolioBuyAmountMax?.maxAmount}
            min={investmentPortfolioBuyAmountMax?.minAmount}
            maxDecimal={investmentPortfolioBuyAmountMax?.decimalPlacesAmount}
            coinName={coinName}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography mr={1} variant="h5" color="text.secondary">
                    {coinName}
                  </Typography>
                </InputAdornment>
              ),
            }}
            error={!!setErrorForMinimumAmount}
            helperText={setErrorForMinimumAmount}
          />
        </Box>

        <Box pt={2}>
          <Typography variant="subtitle1" component="p" color="text.secondary">
            Available Balance Spot:{" "}
            <Typography
              variant="h6"
              component="b"
              color="text.main"
              fontWeight={600}
            >
              {" "}
              <NumberFormats
                value={investmentPortfolioBuyAmountMax?.availableBalance}
              />{" "}
            </Typography>
            {coinName}
          </Typography>

          <Typography variant="subtitle1" component="p" color="text.secondary">
            Fee:{" "}
            <Typography
              variant="h6"
              component="b"
              color="text.main"
              fontWeight={600}
            >
              {" "}
              <NumberFormats
                value={investmentDetail?.objPortfolio?.feePercentageBuy}
              />{" "}
            </Typography>
            %
          </Typography>
        </Box>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6} mt={3} mb={2}>
          <LoadingButton
            color="info"
            fullWidth
            variant="contained"
            onClick={handleClickShowMore}
          >
            Close
          </LoadingButton>
        </Grid>

        <Grid item xs={6} mt={3} mb={2}>
          <LoadingButton
            color={actionButtonBg(theme)}
            disabled={!investment || +investment === 0}
            fullWidth
            variant="contained"
            onClick={handleOpenInvestment}
          >
            Invest
          </LoadingButton>
        </Grid>
      </Grid>

      {openOrderConfirmationModal && (
        <OrderConfirmationModal
          investment={investmentDetail}
          open={openOrderConfirmationModal}
          amount={investment}
          handleClickShowMore={handleClickShowMore}
          onClose={() => {
            setOpenOrderConfirmationModal(false);
          }}
        />
      )}
    </>
  );
}
